import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  TextField,
} from "@mui/material";

const cellSx = {
  backgroundColor: "rgba(47, 53, 71, 0.9)",
  border: "1.5px solid #fff",
  color: "white",
  padding: "10px 8px",
};

const numberSx = {
  ...cellSx,
  textAlign: "left",
  fontWeight: 600,
};

const UnjustifiedDismissalInternalTable = ({
  salary = 0,
  christmasBonus = 0,
  accruedVac = 0,
  proportionalVac = 0,
  notice = 0,
  overtime = 0,
  advanceDeduction = 0,
  otherDeductions = 0,
  severance = 0,
  total = 0,
  onValuesChange, // optional callback when values change
}) => {
  // Editable inputs stored as strings for smooth editing
  const [editableSalary, setEditableSalary] = useState(String(salary));
  const [editableAccruedVac, setEditableAccruedVac] = useState(String(accruedVac));
  const [editableProportionalVac, setEditableProportionalVac] = useState(String(proportionalVac));
  const [editableNotice, setEditableNotice] = useState(String(notice));
  const [editableSeverance, setEditableSeverance] = useState(String(severance));

  // Sync prop changes to state
  useEffect(() => setEditableSalary(String(salary)), [salary]);
  useEffect(() => setEditableAccruedVac(String(accruedVac)), [accruedVac]);
  useEffect(() => setEditableProportionalVac(String(proportionalVac)), [proportionalVac]);
  useEffect(() => setEditableNotice(String(notice)), [notice]);
  useEffect(() => setEditableSeverance(String(severance)), [severance]);

  // Handle input changes with validation (empty or decimal number)
  const handleInputChange = (setter, name) => (event) => {
    const val = event.target.value;
    if (val === "" || /^\d*\.?\d*$/.test(val)) {
      setter(val);
      if (typeof onValuesChange === "function") {
        onValuesChange({
          salary: name === "salary" ? Number(val) || 0 : Number(editableSalary) || 0,
          accruedVac: name === "accruedVac" ? Number(val) || 0 : Number(editableAccruedVac) || 0,
          proportionalVac: name === "proportionalVac" ? Number(val) || 0 : Number(editableProportionalVac) || 0,
          notice: name === "notice" ? Number(val) || 0 : Number(editableNotice) || 0,
          severance: name === "severance" ? Number(val) || 0 : Number(editableSeverance) || 0,
        });
      }
    }
  };

  // Calculate total dynamically (convert strings safely)
  const correctedTotal =
    (Number(editableSalary) || 0) +
    christmasBonus +
    (Number(editableAccruedVac) || 0) +
    (Number(editableProportionalVac) || 0) +
    (Number(editableNotice) || 0) +
    overtime -
    advanceDeduction -
    otherDeductions +
    (Number(editableSeverance) || 0);

  // Helper to format numbers with 2 decimals
  const formatNum = (num) =>
    num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const rows = [
    [
      "Salario Actual",
      <TextField
        value={editableSalary}
        onChange={handleInputChange(setEditableSalary, "salary")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "100px" }}
      />,
      "Durante el último mes o días trabajados",
    ],
    ["Aguinaldo", formatNum(christmasBonus), "Anual/Proporcional"],
    [
      "Vacaciones Causadas",
      <TextField
        value={editableAccruedVac}
        onChange={handleInputChange(setEditableAccruedVac, "accruedVac")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "100px" }}
      />,
      " >1 Año de servicio",
    ],
    [
      "Vacaciones Proporcionales",
      <TextField
        value={editableProportionalVac}
        onChange={handleInputChange(setEditableProportionalVac, "proportionalVac")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "100px" }}
      />,
      "<1 Año de servicio",
    ],
    [
      "Preaviso",
      <TextField
        value={editableNotice}
        onChange={handleInputChange(setEditableNotice, "notice")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "100px" }}
      />,
      "Si no se avisa, se paga aquí",
    ],
    ["Horas Extras", overtime.toLocaleString(undefined, { minimumFractionDigits: 2 }), "Todas las horas extras Autorizadas"],
    [
      "Anticipo",
      advanceDeduction ? `-${advanceDeduction.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "-",
      "Descuentos de adelantos u otros pagos",
    ],
    [
      "Otros Descuentos",
      otherDeductions ? `-${otherDeductions.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "-",
      "Sannciones, Ausencias, etc.",
    ],
    [
      "Indemnización interna",
      <TextField
        value={editableSeverance}
        onChange={handleInputChange(setEditableSeverance, "severance")}
        variant="standard"
        inputProps={{
          style: { color: "white", padding: "6px 8px", fontWeight: 600 },
          inputMode: "decimal",
          autoComplete: "off",
          spellCheck: false,
        }}
        sx={{ "& .MuiInputBase-input": { textAlign: "left", color: "white" }, width: "120px" }}
      />,
      "Si trabajaste 7 meses → fracción > 6 meses → se cuenta como 1 año ",
    ],
  ];

  return (
    <Box mt={4}>
      <Typography variant="h6" mb={2} fontWeight="bold" color="white">
        Despido Injustificado – Interno
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ background: "rgba(47,53,71,0.9)" }}
      >
        <Table>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell sx={cellSx}>{row[0]}</TableCell>
                {/* <TableCell
                  sx={{
                    ...numberSx,
                    color: row[0].toLowerCase().includes("descuento")
                      ? "red"
                      : row[0] === "Internal Severance"
                      ? "#3afa60"
                      : "white",
                    padding: ["Actual Salary", "Accrued Vacations", "Proportional Vacations", "Notice", "Internal Severance"].includes(row[0])
                      ? "6px 8px"
                      : "10px 8px",
                  }}
                >
                  {row[1]}
                </TableCell> */}
                <TableCell
                  sx={{
                    ...numberSx,
                    color: /anticipo|descuento/i.test(row[0])
                      ? "red"
                      : row[0] === "Internal Severance"
                      ? "#3afa60"
                      : "white",
                    padding: [
                      "Actual Salary",
                      "Accrued Vacations",
                      "Proportional Vacations",
                      "Notice",
                      "Internal Severance",
                    ].includes(row[0])
                      ? "6px 8px"
                      : "10px 8px",
                  }}
                >
                  {row[1]}
                </TableCell>
                <TableCell sx={cellSx}>{row[2]}</TableCell>
              </TableRow>
            ))}
            {/* Total row */}
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>
                Total a Pagar
              </TableCell>
              <TableCell
                colSpan={2}
                sx={{
                  ...numberSx,
                  background: "rgba(34,189,110,0.15)",
                  color: "#3afa60",
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: 20,
                }}
              >
                {correctedTotal.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UnjustifiedDismissalInternalTable;
